/**
 * Client-side validation for SFTP SSH private key material (Story 1.3 MVP).
 * When the value is not PEM-shaped, it is treated as a Secrets Manager reference / identifier (not validated here).
 */

const PEM_BEGIN = /-----BEGIN ([A-Z0-9 ]+PRIVATE KEY)-----/;

export function looksLikeSshPrivateKeyPem(value: string): boolean {
    return PEM_BEGIN.test(value.trim());
}

export type SftpSshPrivateKeyValidationResult =
    | { valid: true }
    | { valid: false; userMessage: string };

function base64ToUint8Array(b64: string): Uint8Array | null {
    const cleaned = b64.replace(/\s+/g, '');
    try {
        const bin = atob(cleaned);
        const out = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) {
            out[i] = bin.charCodeAt(i) & 0xff;
        }
        return out;
    } catch {
        return null;
    }
}

function readUint32BE(u8: Uint8Array, offset: number): number {
    return (
        (u8[offset] << 24) | (u8[offset + 1] << 16) | (u8[offset + 2] << 8) | u8[offset + 3]
    ) >>> 0;
}

function readOpenSshString(u8: Uint8Array, offset: number): { value: string; next: number } | null {
    if (offset + 4 > u8.length) return null;
    const len = readUint32BE(u8, offset);
    const start = offset + 4;
    if (start + len > u8.length) return null;
    const slice = u8.subarray(start, start + len);
    const value = new TextDecoder('utf-8', { fatal: false }).decode(slice);
    return { value, next: start + len };
}

/**
 * Returns true when OpenSSH private key blob uses cipher/kdf "none" (unencrypted).
 */
export function openSshPrivateKeyBlobIsUnencrypted(armored: string): boolean | 'invalid' {
    const m = armored.match(
        /-----BEGIN OPENSSH PRIVATE KEY-----([\s\S]*?)-----END OPENSSH PRIVATE KEY-----/i
    );
    if (!m) return false;
    const u8 = base64ToUint8Array(m[1]);
    if (!u8 || u8.length < 32) return 'invalid';
    const magic = new TextDecoder().decode(u8.subarray(0, 15));
    if (magic !== 'openssh-key-v1\0') return 'invalid';
    let off = 15;
    const cipher = readOpenSshString(u8, off);
    if (!cipher) return 'invalid';
    off = cipher.next;
    const kdf = readOpenSshString(u8, off);
    if (!kdf) return 'invalid';
    off = kdf.next;
    const kdfOpts = readOpenSshString(u8, off);
    if (!kdfOpts) return 'invalid';
    off = kdfOpts.next;
    return cipher.value === 'none' && kdf.value === 'none';
}

/** PKCS#8 / PKCS#1 RSA OID bytes (1.2.840.113549.1.1.1) inside DER */
const RSA_OID_MARKER = new Uint8Array([
    0x06, 0x09, 0x2a, 0x86, 0x48, 0x86, 0xf7, 0x0d, 0x01, 0x01, 0x01,
]);

function uint8ArrayIncludes(haystack: Uint8Array, needle: Uint8Array): boolean {
    outer: for (let i = 0; i <= haystack.length - needle.length; i++) {
        for (let j = 0; j < needle.length; j++) {
            if (haystack[i + j] !== needle[j]) {
                continue outer;
            }
        }
        return true;
    }
    return false;
}

function extractPemBody(armored: string, label: string): string | null {
    const re = new RegExp(
        `-----BEGIN ${label}-----([\\s\\S]*?)-----END ${label}-----`,
        'i'
    );
    const m = armored.match(re);
    return m ? m[1] : null;
}

function traditionalPemLooksEncrypted(pem: string): boolean {
    const head = pem.slice(0, 800);
    if (/Proc-Type:\s*4\s*,\s*ENCRYPTED/i.test(head)) return true;
    if (/DEK-Info:/i.test(head)) return true;
    if (/-----BEGIN ENCRYPTED PRIVATE KEY-----/i.test(pem)) return true;
    return false;
}

/**
 * Validates trimmed private key material when it looks like a PEM private key.
 * Non-PEM values are considered references and return { valid: true }.
 */
export function validateSftpSshPrivateKeyMaterial(trimmed: string): SftpSshPrivateKeyValidationResult {
    const v = trimmed.trim();
    if (!v || v === '*****') {
        return { valid: true };
    }

    if (!looksLikeSshPrivateKeyPem(v)) {
        return { valid: true };
    }

    if (/-----BEGIN EC PRIVATE KEY-----/i.test(v)) {
        return {
            valid: false,
            userMessage: 'Elliptic curve (EC) private keys are not supported for MVP. Use an OpenSSH RSA key.',
        };
    }

    if (traditionalPemLooksEncrypted(v)) {
        return {
            valid: false,
            userMessage:
                'Encrypted private keys are not supported. Use an unencrypted OpenSSH RSA key for MVP.',
        };
    }

    if (/-----BEGIN RSA PRIVATE KEY-----/i.test(v)) {
        const body = extractPemBody(v, 'RSA PRIVATE KEY');
        if (!body) {
            return { valid: false, userMessage: 'RSA private key PEM appears incomplete (missing END marker).' };
        }
        if (!base64ToUint8Array(body.replace(/\s+/g, ''))) {
            return {
                valid: false,
                userMessage: 'RSA private key PEM could not be decoded (invalid base64 in key body).',
            };
        }
        return { valid: true };
    }

    if (/-----BEGIN OPENSSH PRIVATE KEY-----/i.test(v)) {
        const dec = openSshPrivateKeyBlobIsUnencrypted(v);
        if (dec === 'invalid') {
            return {
                valid: false,
                userMessage: 'This OpenSSH private key block could not be read. Check that the full key was pasted.',
            };
        }
        if (dec !== true) {
            return {
                valid: false,
                userMessage:
                    'Encrypted OpenSSH keys are not supported. Export or generate an unencrypted RSA key for MVP.',
            };
        }
        const u8 = base64ToUint8Array(
            (v.match(/-----BEGIN OPENSSH PRIVATE KEY-----([\s\S]*?)-----END OPENSSH PRIVATE KEY-----/i) ||
                [])[1] || ''
        );
        if (!u8) {
            return { valid: false, userMessage: 'The OpenSSH private key could not be decoded (invalid base64).' };
        }
        const txt = new TextDecoder('utf-8', { fatal: false }).decode(u8);
        if (!txt.includes('ssh-rsa')) {
            return {
                valid: false,
                userMessage: 'MVP supports RSA (ssh-rsa) keys only. This OpenSSH key does not look like an RSA key.',
            };
        }
        return { valid: true };
    }

    if (/-----BEGIN PRIVATE KEY-----/i.test(v)) {
        const body = extractPemBody(v, 'PRIVATE KEY');
        if (!body) {
            return { valid: false, userMessage: 'PKCS#8 private key block appears incomplete.' };
        }
        const u8 = base64ToUint8Array(body);
        if (!u8) {
            return { valid: false, userMessage: 'PKCS#8 private key could not be decoded (invalid base64).' };
        }
        if (!uint8ArrayIncludes(u8, RSA_OID_MARKER)) {
            return {
                valid: false,
                userMessage:
                    'PKCS#8 key is not RSA (or could not be detected). MVP supports unencrypted OpenSSH RSA keys.',
            };
        }
        return { valid: true };
    }

    return {
        valid: false,
        userMessage:
            'Unrecognized private key format. MVP expects an unencrypted RSA key (PEM: BEGIN RSA PRIVATE KEY, BEGIN OPENSSH PRIVATE KEY, or PKCS#8 RSA BEGIN PRIVATE KEY) or a Secrets Manager reference.',
    };
}
